import * as Yup from 'yup';

export type TContactType = 'professional' | 'personal';

// ── Explicit type (Yup.InferType cannot resolve computed property names) ────

export interface IDuplicateAddressFields {
  ContactType: TContactType;
  Company: string;
  FirstName: string;
  LastName: string;
  Street: string;
  City: string;
  PostalCode: string;
  CountryCode: string;
  PhoneNumber: string;
  Email: string;
}

type Prefixed<P extends string, T> = {
  [K in keyof T as `${P}${K & string}`]: T[K];
};

export interface IDuplicateFormValues
  extends
    Prefixed<'sender', IDuplicateAddressFields>,
    Prefixed<'recipient', IDuplicateAddressFields>,
    Prefixed<'returnSender', IDuplicateAddressFields>,
    Prefixed<'returnRecipient', IDuplicateAddressFields> {
  partnerId: string;
  selectedPartner: string;
  serviceType: string;
  boxId: string;
  boxLabel: string;
  carrier: string;
  reference: string;
  carrierTrackingCode: string;
  weightInGrams: string;
  addReturn: boolean;
  returnCarrier: string;
  returnReference: string;
  returnCarrierTrackingCode: string;
}

// ── Shared constants ────────────────────────────────────────────────────────

/** Shared between flattenAddress / collectAddress / addressFields to stay in sync */
export const ADDRESS_SUFFIXES = [
  'ContactType',
  'Company',
  'FirstName',
  'LastName',
  'Street',
  'City',
  'PostalCode',
  'CountryCode',
  'PhoneNumber',
  'Email',
] as const;

export type TAddressSuffix = (typeof ADDRESS_SUFFIXES)[number];

// ── Schema helpers ──────────────────────────────────────────────────────────

const addressFields = (prefix: string) => ({
  [`${prefix}ContactType`]: Yup.string()
    .oneOf(['professional', 'personal'] as const)
    .required(),
  [`${prefix}Company`]: Yup.string().when(`${prefix}ContactType`, {
    is: 'professional',
    then: (s) => s.required('Company name is required'),
    otherwise: (s) => s,
  }),
  [`${prefix}FirstName`]: Yup.string().when(`${prefix}ContactType`, {
    is: 'personal',
    then: (s) => s.required('First name is required'),
    otherwise: (s) => s,
  }),
  [`${prefix}LastName`]: Yup.string().when(`${prefix}ContactType`, {
    is: 'personal',
    then: (s) => s.required('Last name is required'),
    otherwise: (s) => s,
  }),
  [`${prefix}Street`]: Yup.string().required('Address is required'),
  [`${prefix}City`]: Yup.string().required('City / Town is required'),
  [`${prefix}PostalCode`]: Yup.string().required('Zipcode is required'),
  [`${prefix}CountryCode`]: Yup.string(),
  [`${prefix}PhoneNumber`]: Yup.string(),
  [`${prefix}Email`]: Yup.string()
    .email('Invalid email format')
    .required('Notification email is required'),
});

const conditionalAddressFields = (prefix: string) =>
  Object.fromEntries(
    Object.entries(addressFields(prefix)).map(([key, schema]) => [
      key,
      Yup.string().when('addReturn', {
        is: true,
        then: () => schema,
        otherwise: () => Yup.string().optional(),
      }),
    ])
  );

// ── Schema builder ──────────────────────────────────────────────────────────

export const buildDuplicateFormSchema = () =>
  Yup.object({
    // Partnership
    partnerId: Yup.string().required('Partner is required'),
    selectedPartner: Yup.string(),
    serviceType: Yup.string(),

    // Box
    boxId: Yup.string(),
    boxLabel: Yup.string(),

    // Outbound parcel
    carrier: Yup.string().required(),
    reference: Yup.string(),
    carrierTrackingCode: Yup.string(),
    weightInGrams: Yup.string().matches(
      /^\d*\.?\d*$/,
      'Weight must be a valid number'
    ),

    // Outbound sender
    ...addressFields('sender'),

    // Outbound recipient
    ...addressFields('recipient'),

    // Return toggle
    addReturn: Yup.boolean().required(),

    // Return carrier
    returnCarrier: Yup.string(),
    returnReference: Yup.string(),
    returnCarrierTrackingCode: Yup.string(),

    // Return sender (conditional on addReturn)
    ...conditionalAddressFields('returnSender'),

    // Return recipient (conditional on addReturn)
    ...conditionalAddressFields('returnRecipient'),
  });
